import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { isOrgAdmin } from '../components/Layout';
import { Button, Card, CopyField, cx, Dialog, Empty, ErrorNote, Field, inputClass, Loading, PageHeader, Section } from '../components/ui';
import { api, unwrap, type ApiKey, type ApiKeys as ApiKeysData, type NewApiKey } from '../lib/api';
import { fmtAgo, fmtDate, fmtTime } from '../lib/format';
import { useApiKeys, useManagedOrgs } from '../lib/queries';

const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
const VALIDITY = [30, 90, 180, 365] as const;

export function ApiKeys() {
  const { me, vardexAdmin, orgs } = useManagedOrgs();
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org') ?? orgs[0]?.id;
  const org = orgs.find((o) => o.id === orgId);
  const keys = useApiKeys(orgId);

  if (me.isPending) return <Loading />;
  if (me.data && !isOrgAdmin(me.data) && !vardexAdmin) return <Empty>Only organization admins manage API keys.</Empty>;

  return (
    <>
      <PageHeader
        title="API keys"
        subtitle={org?.name}
        actions={
          orgs.length > 1 && (
            <select className={cx(inputClass, 'w-auto')} value={orgId} onChange={(e) => setParams({ org: e.target.value })} aria-label="Organization">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )
        }
      />
      {!orgId ? <Empty>No organization.</Empty> : keys.isPending ? <Loading /> : keys.error ? <ErrorNote error={keys.error} /> : (
        // Vardex admins who aren't members may revoke keys, but only the organization's own admins create them.
        <KeysView org={orgId} data={keys.data} canCreate={!!me.data && isOrgAdmin(me.data, orgId)} />
      )}
    </>
  );
}

function status(k: ApiKey): string {
  if (k.status === 'revoked') return 'Revoked';
  if (k.status === 'expired') return 'Expired';
  return `Active until ${fmtDate(k.expiresAt, localTz)}`;
}

function KeysView({ org, data, canCreate }: { org: string; data: ApiKeysData; canCreate: boolean }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['api-keys', org] });
  const [creating, setCreating] = useState(false);
  const plantName = new Map(data.plants.map((p) => [p.id, p.name]));

  const revoke = useMutation({
    mutationFn: (k: ApiKey) => unwrap(api.POST('/api-keys/{id}/revoke', { params: { path: { id: k.id } } })),
    onSuccess: refresh,
  });

  return (
    <>
      <Section title="Keys" actions={canCreate && <Button variant="primary" onClick={() => setCreating(true)}>Create key</Button>}>
        <p className="mb-3 text-sm text-muted">
          For scripts and integrations. A key reads data from the plants you pick (latest values, history, alarms) and nothing else.
          It belongs to the organization, so it keeps working if the person who created it leaves. <Link to="/connect" className="text-accent underline">How to use a key</Link>
        </p>
        {!canCreate && <p className="mb-3 text-sm text-muted">Keys are created by the organization's own admins. You can revoke them.</p>}
        <ErrorNote error={revoke.error} />
        {data.data.length === 0 ? <Empty>No API keys yet.</Empty> : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Plants</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Last used</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.data.map((k) => (
                  <tr key={k.id} className={cx('border-t border-rule', k.status !== 'active' && 'text-muted')}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{k.name}</div>
                      <div className="tabular text-xs text-muted">{k.prefix}…{k.createdBy && ` · by ${k.createdBy}`}</div>
                    </td>
                    <td className="px-3 py-2">{k.plantIds.map((id) => plantName.get(id) ?? id).join(', ')}</td>
                    <td className="px-3 py-2">{status(k)}</td>
                    <td className="px-3 py-2" title={k.lastUsedAt ? fmtTime(k.lastUsedAt, localTz, { seconds: true }) : undefined}>
                      {k.lastUsedAt ? fmtAgo(k.lastUsedAt) : 'Never'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {k.status === 'active' && (
                        <Button
                          variant="ghost"
                          className="text-critical"
                          onClick={() => { if (confirm(`Revoke ${k.name}? Anything using it stops working at once.`)) revoke.mutate(k); }}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </Section>

      <Dialog open={creating} onClose={() => { setCreating(false); refresh(); }} title="Create API key">
        <CreateKeyForm org={org} plants={data.plants} onDone={refresh} />
      </Dialog>
    </>
  );
}

function CreateKeyForm({ org, plants, onDone }: { org: string; plants: ApiKeysData['plants']; onDone: () => void }) {
  const [name, setName] = useState('');
  const [plantIds, setPlantIds] = useState<number[]>(plants.length === 1 ? [plants[0]!.id] : []);
  const [validDays, setValidDays] = useState<number>(90);
  const create = useMutation({
    mutationFn: () => unwrap(api.POST('/organizations/{org}/api-keys', { params: { path: { org } }, body: { name, plantIds, validDays } })),
    onSuccess: onDone,
  });
  if (create.data) {
    const slug = plants.find((p) => p.id === plantIds[0])?.slug ?? '<plant>';
    return <NewKey created={create.data} slug={slug} />;
  }
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
      <Field label="Name" hint="What uses the key, so you know what breaks if you revoke it.">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Power BI" maxLength={100} required autoFocus />
      </Field>
      <Field label="Plants">
        {plants.length === 0 ? <p className="text-sm text-muted">This organization has no plants yet.</p> : (
          <div className="space-y-1 text-sm">
            {plants.map((p) => (
              <label key={p.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={plantIds.includes(p.id)}
                  onChange={(e) => setPlantIds(e.target.checked ? [...plantIds, p.id] : plantIds.filter((x) => x !== p.id))}
                  className="accent-[var(--accent)]"
                />
                {p.name} <span className="text-muted">{p.slug}</span>
              </label>
            ))}
          </div>
        )}
      </Field>
      <Field label="Valid for" hint="Create a new key before this one expires.">
        <select className={cx(inputClass, 'w-auto')} value={validDays} onChange={(e) => setValidDays(Number(e.target.value))}>
          {VALIDITY.map((d) => <option key={d} value={d}>{d === 365 ? '1 year' : `${d} days`}</option>)}
        </select>
      </Field>
      <ErrorNote error={create.error} />
      <Button type="submit" variant="primary" className="mt-2" disabled={create.isPending || plantIds.length === 0}>Create key</Button>
    </form>
  );
}

/** Shown once, right after creating the key. */
function NewKey({ created, slug }: { created: NewApiKey; slug: string }) {
  const url = `${window.location.origin}/v1/plants/${slug}/latest`;
  return (
    <div className="space-y-3 text-sm">
      <div>
        <div className="mb-1 font-medium">API key</div>
        <CopyField value={created.key} label="API key" />
        <p className="mt-1 text-xs text-muted">
          Shown only now. Store it where the integration keeps its secrets, never in code or a shared document. Valid until {fmtDate(created.expiresAt, localTz)}.
        </p>
      </div>
      <div>
        <div className="mb-1 font-medium">Try it</div>
        <CopyField value={`curl -H "Authorization: Bearer ${created.key}" ${url}`} label="curl command" />
      </div>
    </div>
  );
}
